import { Upload, Plus, UserPlus, X, Paperclip } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import { CarImageHolder } from '@components/car-image-holder'
import { ClientForm } from '@components/client'
import { VehicleInfoDetails } from '@components/vehicle-info-details'
import { VinInput } from '@components/vin'
import {
  AdasBox,
  AdasButton,
  AdasContainer,
  AdasRadioGroup,
  AdasSelect,
  AdasTextField,
  AdasTypography,
  AdasAutoComplete
} from '@components/wrapper-components'
import {
  requestCreateQuote,
  useSubmitFileAttachment,
  getAttachmentAppointmentEstimate,
  getAttachmentAppointmentCalibration
} from '../../../api/api'
import { VehicleInfoManuallPage } from '../../vehicle-info-page/VehicleInfoManuallPage'
import { getQuoteSummaryUrl } from '../../../constants'
import { ATTACHMENT_TYPE_MAP, MIME_TYPES_MAP } from '../../../variable-maps'
import { Logger } from '../../../logger'
import { useClientAndInsuranceData } from '@hooks/useClientAndInsuranceData'
import { useInitReferralAndInsuranceFromState } from '@hooks/useInitReferralAndInsuranceFromState'
import { useReferralAutoMatch } from '@hooks/useReferralAutoMatch'
import { useVehicleInProgress } from '@hooks/useVehicleInProgress'
import { FormModal } from '@components/branded-modal'
import {
  MAX_FUZZY_SCORE,
  fuzzyMatchClients,
  fuzzyMatchInsurers,
  matchByName,
  matchWithFuzzy,
  sharesADistinctiveWord
} from '../../../utils/fuzzyMatchClients'

const StepBadge = ({ n }) => (
  <span className='inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#0A1628] text-[12px] font-bold text-white'>
    {n}
  </span>
)
StepBadge.propTypes = { n: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }

const FieldLabel = ({ children, required }) => (
  <AdasTypography className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>
    {children}
    {required && <span className='ml-0.5 text-[#E24B4A]'>*</span>}
  </AdasTypography>
)
FieldLabel.propTypes = { children: PropTypes.node, required: PropTypes.bool }

const DocumentUploadRow = ({ name, title, subtitle, existingData, onChange }) => {
  const fileRef = useRef(null)
  const addMessage = useMessageStore((store) => store.addMessage)

  const handleUpload = () => {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    const supported = Object.values(MIME_TYPES_MAP)
    if (!supported.includes(file.type)) {
      addMessage({ text: 'File format not supported!' })
      return
    }
    onChange({ data: file, name })
  }

  const handleRemove = () => onChange({ data: '', name })

  return (
    <AdasBox className='flex items-center gap-3 rounded-lg border border-[#EBEDF0] bg-white px-4 py-3'>
      <AdasBox className='flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#E8EFFF]'>
        {existingData ? <Paperclip className='h-4 w-4 text-[#1F6FEB]' /> : <Upload className='h-4 w-4 text-[#1F6FEB]' />}
      </AdasBox>
      <AdasBox className='min-w-0 flex-1'>
        {existingData ? (
          <>
            <a
              href={URL.createObjectURL(existingData)}
              target='_blank'
              rel='noopener noreferrer'
              className='block truncate text-[14px] font-semibold text-[#0A1628] hover:underline'
            >
              {existingData.name}
            </a>
            <AdasTypography className='truncate text-[12px] text-[#6B7280]'>{subtitle}</AdasTypography>
          </>
        ) : (
          <>
            <AdasTypography className='text-[14px] font-semibold text-[#0A1628]'>{title}</AdasTypography>
            <AdasTypography className='text-[12px] text-[#6B7280]'>{subtitle}</AdasTypography>
          </>
        )}
      </AdasBox>
      {existingData ? (
        <button
          type='button'
          onClick={handleRemove}
          aria-label='Remove file'
          className='inline-flex h-8 w-8 items-center justify-center rounded-md text-[#475569] hover:bg-[#F8FAFC]'
        >
          <X className='h-4 w-4' />
        </button>
      ) : (
        <label className='inline-flex cursor-pointer items-center justify-center rounded-full bg-[#1F6FEB] px-4 py-1.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1B5DD0]'>
          Upload
          <input ref={fileRef} type='file' accept='.jpg, .jpeg, .png, .pdf, .doc, .docx, .txt' hidden onChange={handleUpload} />
        </label>
      )}
    </AdasBox>
  )
}
DocumentUploadRow.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  existingData: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

/**
 * The client New Quote pre-selects for a name handed in with the quote: the client with exactly that
 * name, or — when the quote is launched from an ADAS Intelligence estimate — the best fuzzy match
 * provided it is confident (matchWithFuzzy). An estimate from a body shop that is not one of the
 * center's clients resolves to nothing: the dropdown stays empty rather than showing the client that
 * merely scored first ("Schneider Auto Karosserie Body & Paint" scores 0.775 against "Car Star Eden
 * Prairie", which is no match at all).
 */
export const preselectedClientFor = (clients, extractedClientName, fromEstimate) => {
  if (!extractedClientName || !clients?.length) return undefined
  return fromEstimate ? matchWithFuzzy(clients, extractedClientName, fuzzyMatchClients) : matchByName(clients, extractedClientName)
}

export const NewQuote = (props) => {
  const [isAddClient, setIsAddClient] = useState(false)
  const [vehicleData, setVehicleData] = useState({})
  const [displayDetails, setDisplayDetails] = useState(false)
  const [customerPay, setCustomerPay] = useState(false)
  const [isVinAvailable, setIsVinAvailable] = useState(true)
  const [preselectedInsuranceId, setPreselectedInsuranceId] = useState(null)
  const [documents, setDocuments] = useState({
    preCalRepairEstimateScan: '',
    calRequiredReport: ''
  })
  const location = useLocation()

  const referralSourceCode = location.state?.referral
  const prePopulateVin = location.state?.prepopulateVin || location.state?.vin
  const [formInputValues, setFormInputValues] = useState({
    client: '',
    roNumber: '',
    serviceType: 'STANDARD',
    vin: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleTrim: '',
    insurance_company_id: '',
    appointment_id: '',
    referral_source_code: referralSourceCode || null
  })
  const { uploadData } = useSubmitFileAttachment()
  const ccId = useCenterStore((store) => store.ccId)
  const {
    clients,
    insuranceCompanies,
    fetchClients,
    clearVehicleFields,
    fetchReferralSource,
    referralSourceList,
    handleVehicleManualChange
  } = useClientAndInsuranceData(ccId)
  const isEnable = useMemo(() => {
    const vinValid =
      isVinAvailable === true
        ? !!formInputValues.vin && !!formInputValues.client
        : !!formInputValues.client &&
          !!formInputValues.vehicleYear &&
          !!formInputValues.vehicleMake &&
          !!formInputValues.vehicleModel &&
          !!formInputValues.vehicleTrim

    const roNumberValid = customerPay || formInputValues.roNumber
    const isInsuranceCompanyValid = !!formInputValues.insurance_company_id
    return vinValid && roNumberValid && isInsuranceCompanyValid
  }, [
    isVinAvailable,
    formInputValues.vin,
    formInputValues.client,
    formInputValues.vehicleYear,
    formInputValues.vehicleMake,
    formInputValues.vehicleModel,
    formInputValues.vehicleTrim,
    formInputValues.roNumber,
    customerPay,
    formInputValues.insurance_company_id
  ])

  const uploadFile = useCallback(
    async (file, type, id) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      let attachmentType = ATTACHMENT_TYPE_MAP[type]
      const params = {
        data: formData,
        workorderId: id,
        type: 'quotes',
        attachmentType
      }

      const response = uploadData(params)
      response.then(() => {})
    },
    [uploadData]
  )

  const title = props.title

  const history = useHistory()
  const redirectQuoteSummary = useCallback(
    (data) => {
      // Replace (not push) so the just-submitted New Quote form leaves the history stack.
      // Otherwise "Back to Quotes" on the summary (history.goBack) returns to this form
      // instead of the Quotes list — and back-nav could re-open a stale creation form.
      history.replace(getQuoteSummaryUrl(data?.quote?.reference_number))
    },
    [history]
  )

  const documentChangeHandler = useCallback((props) => {
    setDocuments((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [props.name]: props.data
      }
      return updatedValues
    })
  }, [])

  const changeHandler = useCallback((e) => {
    const fieldValue = e.target.name === 'year' ? e.target.value.replace(/\D/g, '') : e.target.value
    setFormInputValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [e.target.name]: fieldValue
      }
      return updatedValues
    })
  }, [])

  const { autoMatchedReferral, setAutoMatchedReferral, showReferralField, setShowReferralField, matchReferral } = useReferralAutoMatch({
    insuranceCompanies,
    referralSourceList,
    setFormInputValues,
    referralSourceCode
  })
  useInitReferralAndInsuranceFromState({
    referralSourceCode,
    insuranceCompanies,
    setFormInputValues,
    setAutoMatchedReferral: setAutoMatchedReferral, // optional, only in VehicleInfoPage
    setShowReferralField,
    ccId: ccId
  })
  const handleShowReferralField = useCallback(
    (e) => {
      const isYesSelected = e.target.value === 'true'
      setShowReferralField(isYesSelected)

      setFormInputValues((prev) => {
        const updated = { ...prev }

        if (isYesSelected) {
          matchReferral(prev.insurance_company_id)
        } else {
          updated.referral_source_code = null
        }

        return updated
      })
    },
    [matchReferral]
  )

  const handleCustomerPay = useCallback((e) => {
    const value = /true/.test(e.target.value)
    const roNumber = value ? 'Customer-Pay' : ''
    setCustomerPay(value)
    setFormInputValues((prevValues) => {
      return { ...prevValues, roNumber: roNumber }
    })
  }, [])

  const getVehicleInfo = useCallback(() => {
    if (isVinAvailable === true) {
      const { color, imageUrl, fuel, safetySystems, ...vehicle } = vehicleData
      return {
        vin: formInputValues.vin,
        calibration_center_id: ccId,
        exterior_color: color,
        image_url: imageUrl,
        fuel_capacity_gal: fuel,
        safetySystemsItems: safetySystems,
        ...vehicle
      }
    } else {
      const { vehicleYear, vehicleMake, vehicleModel, vehicleTrim } = formInputValues
      return {
        year: vehicleYear,
        make: vehicleMake,
        model: vehicleModel,
        trim: vehicleTrim,
        vin: ''
      }
    }
  }, [ccId, formInputValues, isVinAvailable, vehicleData])

  const { handleCreateError, renderVehicleInProgress } = useVehicleInProgress('quote')

  const submitHandler = useCallback(async () => {
    // When this quote was launched from an ADAS Intelligence estimate, estimateSource.id
    // carries the scrub_estimate_id (UUID). Passing source=ADAS_INTELLIGENCE + that id tells
    // the BE to auto-quote: it maps the estimate's engine cards to AUTO_QUOTE calibration /
    // service lines (returned alongside the STANDARD default lines). Plain New Quote omits
    // both fields and stays on the STANDARD profile.
    const scrubEstimateId = location.state?.estimateSource?.id
    const quoteRequestData = {
      client_id: formInputValues.client,
      calibration_center_id: ccId,
      repair_order_num: formInputValues.roNumber,
      type: formInputValues.serviceType,
      insurance_company_id: formInputValues.insurance_company_id,
      appointment_id: formInputValues.appointment_id,
      vehicle: getVehicleInfo(),
      referral_source_code: formInputValues.referral_source_code,
      ...(scrubEstimateId ? { source: 'ADAS_INTELLIGENCE', scrub_estimate_id: scrubEstimateId } : {})
    }

    try {
      const response = await requestCreateQuote({ data: quoteRequestData })
      if (response?.status === 201) {
        const id = response.data.quote.id
        Object.keys(documents).map((doc) => {
          if (documents[doc]) {
            uploadFile(documents[doc], doc, id)
          }
        })

        // The BE links the scrub estimate to the new quote in the same transaction (scrub_estimate_id
        // above), so the Records column / Linked Quote button pick it up with no follow-up call. A 409 for
        // the same job (body shop, RO, VIN) still open opens the Vehicle Already In Progress dialog.

        redirectQuoteSummary(response.data)
      }
    } catch (err) {
      if (handleCreateError(err)) return
      Logger.error({ message: err, payload: { file: 'NewQuote', method: 'requestAuth', quoteRequestData } })
    }
  }, [
    ccId,
    handleCreateError,
    documents,
    formInputValues.client,
    formInputValues.roNumber,
    formInputValues.serviceType,
    getVehicleInfo,
    redirectQuoteSummary,
    uploadFile,
    formInputValues.insurance_company_id,
    location.state
  ])

  const autocompleteHandler = useCallback(
    (event) => {
      const { name, value } = event.target

      setFormInputValues((prevValues) => ({
        ...prevValues,
        [name]: value || ''
      }))

      // Match referral only when changing insurance
      if (name === 'insurance_company_id') {
        matchReferral(value)
      }
    },
    [matchReferral, setFormInputValues]
  )

  const updateVehicleInfo = useCallback((props) => {
    setDisplayDetails(true)
    setVehicleData({ ...props.data, safetySystemsItems: props.data?.safetySystems })
    setFormInputValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        vin: props.vin
      }
      return updatedValues
    })
  }, [])

  useEffect(() => {
    if (location.state?.appointment_id || location.state?.aro) {
      setFormInputValues((prevState) => ({
        ...prevState,
        roNumber: location.state?.aro || prevState.roNumber,
        appointment_id: location.state?.appointment_id || prevState.appointment_id
      }))
    }
  }, [location.state?.appointment_id, location.state?.aro, setFormInputValues])

  // Pre-select the appointment's client once the client list has loaded (Radix Select
  // only reflects a value whose option is mounted). Match by saved client id when
  // present, otherwise by the appointment's business/full name (external bookings carry
  // the name, not a saved client id), with a fuzzy fallback. Skips when a client is
  // already chosen (e.g. the ADAS Intelligence estimate auto-match above).
  useEffect(() => {
    if (!clients?.length) return
    const clientId = location.state?.clientId
    const apptName = location.state?.businessName || location.state?.fullName || ''
    let match = clientId ? clients.find((c) => String(c.id) === String(clientId)) : null
    if (!match && apptName) {
      // Exact name, else a confident fuzzy hit sharing a distinctive word; otherwise leave it for the user.
      match = matchWithFuzzy(clients, apptName, fuzzyMatchClients) || null
    }
    if (match) setFormInputValues((prev) => (prev.client ? prev : { ...prev, client: match.id }))
  }, [location.state?.clientId, location.state?.businessName, location.state?.fullName, clients])

  // Pre-attach calibration report file when handed in from ADAS Intelligence
  useEffect(() => {
    if (location.state?.calibrationReportFile instanceof File) {
      setDocuments((prev) => ({ ...prev, calRequiredReport: location.state.calibrationReportFile }))
    }
    if (location.state?.estimateReportFile instanceof File) {
      setDocuments((prev) => ({ ...prev, preCalRepairEstimateScan: location.state.estimateReportFile }))
    }
  }, [location.state?.calibrationReportFile, location.state?.estimateReportFile])

  // Preselect the insurance company when handed in from ADAS Intelligence. Prefer the
  // backend-resolved canonical id (CAR-3767); fall back to matching the extracted name string
  // for older hand-ins that only carry insuranceName. Waits on insuranceCompanies to load so
  // AdasAutoComplete can reflect the value (its option must be mounted).
  useEffect(() => {
    if (!insuranceCompanies?.length) return
    const handInId = location.state?.insuranceCompanyId
    const handInName = location.state?.insuranceName
    let match = handInId != null ? insuranceCompanies.find((c) => c.id === handInId) : null
    if (!match && handInName) {
      // Exact name, then the confident fuzzy hit the queue's bulk quote also uses.
      match = matchWithFuzzy(insuranceCompanies, handInName, fuzzyMatchInsurers) ?? null
    }
    if (match) {
      setFormInputValues((prev) => (prev.insurance_company_id ? prev : { ...prev, insurance_company_id: match.id }))
      setPreselectedInsuranceId(match.id)
    }
  }, [location.state?.insuranceCompanyId, location.state?.insuranceName, insuranceCompanies])

  // The preselect above bypasses handleInputChange, so match its national agreement here (once the
  // referral list has loaded); otherwise the quote is created without its referral code and is not
  // treated as a National Agreement quote. Skipped when a referral was handed in, or once the user picks
  // a different insurer.
  useEffect(() => {
    if (referralSourceCode || preselectedInsuranceId == null || !referralSourceList.length) return
    if (formInputValues.insurance_company_id !== preselectedInsuranceId) return
    matchReferral(preselectedInsuranceId)
  }, [referralSourceCode, preselectedInsuranceId, referralSourceList, formInputValues.insurance_company_id, matchReferral])

  // Auto Quote mode: this quote was launched from Estimate Summary's "Create Quote" so
  // the extracted client name comes through in location.state. The dropdown still shows
  // every client returned for the calibration center — we just sort the fuzzy matches to
  // the top and preselect the best one (CAR-3459). Plain New Quote keeps the original
  // unfiltered, unsorted list.
  const isAutoQuoteFromEstimate = !!location.state?.estimateSource?.id
  const extractedClientName = location.state?.clientName || ''
  // Only hits confident enough to stand for the extracted name (MAX_FUZZY_SCORE, the queue's bar). Fuse
  // returns something for almost any string — "Schneider Auto Karosserie Body & Paint" scores 0.775
  // against "Car Star Eden Prairie" — and a weak hit must neither be pre-selected nor sorted to the top.
  const matchedClients = useMemo(() => {
    if (!isAutoQuoteFromEstimate || !extractedClientName || !clients?.length) return []
    return fuzzyMatchClients(extractedClientName, clients).filter(
      (c) => c.fuzzyScore <= MAX_FUZZY_SCORE && sharesADistinctiveWord(extractedClientName, c.name || c.value)
    )
  }, [isAutoQuoteFromEstimate, extractedClientName, clients])
  const clientDropdownOptions = useMemo(() => {
    if (!isAutoQuoteFromEstimate || !extractedClientName || !clients?.length) return clients || []
    if (matchedClients.length === 0) return clients
    const matchedIds = new Set(matchedClients.map((c) => c.id))
    const rest = clients.filter((c) => !matchedIds.has(c.id))
    return [...matchedClients, ...rest]
  }, [isAutoQuoteFromEstimate, extractedClientName, clients, matchedClients])
  // Auto-match client by name (preselectedClientFor). No match leaves the dropdown empty for the user
  // to pick; it is never pre-populated with whichever client happened to score first.
  useEffect(() => {
    const target = preselectedClientFor(clients, extractedClientName, isAutoQuoteFromEstimate)
    if (target) {
      setFormInputValues((prev) => (prev.client ? prev : { ...prev, client: target.id }))
    }
  }, [extractedClientName, clients, isAutoQuoteFromEstimate])

  useEffect(() => {
    const fetchEstimateFile = async (value) => {
      try {
        const response = await getAttachmentAppointmentEstimate(ccId, value.appointment_id)
        const fileFromResponse = new File([response.data], value.name, {
          type: MIME_TYPES_MAP[`.${value.file_extension}`]
        })
        fileFromResponse.id = value.id

        setDocuments((prevValues) => ({
          ...prevValues,
          preCalRepairEstimateScan: fileFromResponse
        }))
      } catch (error) {
        Logger.error({ message: error.message, payload: { value } })
      }
    }

    const fetchCalibrationFile = async (value) => {
      try {
        const response = await getAttachmentAppointmentCalibration(ccId, value.appointment_id)
        const fileFromResponse = new File([response.data], value.name, {
          type: MIME_TYPES_MAP[`.${value.file_extension}`]
        })
        fileFromResponse.id = value.id

        setDocuments((prevValues) => ({
          ...prevValues,
          calRequiredReport: fileFromResponse
        }))
      } catch (error) {
        Logger.error({ message: error.message, payload: { value } })
      }
    }

    if (location.state?.recentEstimate?.id) {
      fetchEstimateFile(location.state.recentEstimate)
    }

    if (location.state?.calibrationFile?.id) {
      fetchCalibrationFile(location.state.calibrationFile)
    }
  }, [location.state?.recentEstimate?.id, location.state?.calibrationFile?.id])

  const handleVinAvailableToggle = useCallback(
    (e) => {
      setIsVinAvailable(e.target.value === 'true')
      setDisplayDetails('')
      setVehicleData('')
      setFormInputValues((prevValues) => ({
        ...prevValues,
        vin: ''
      }))
    },
    [setFormInputValues]
  )

  useEffect(() => {
    fetchClients()
  }, [fetchClients])

  useEffect(() => {
    fetchReferralSource()
  }, [fetchReferralSource])

  const handleVehicleManuallChange = (eventOrValue) => {
    handleVehicleManualChange(eventOrValue, setFormInputValues)
  }

  useEffect(() => {
    clearVehicleFields(setFormInputValues)
  }, [isVinAvailable])

  return (
    <AdasContainer maxWidth='lg'>
      <AdasBox className='mb-[60px] mt-5'>
        {isAddClient && (
          <FormModal open={isAddClient} onClose={() => setIsAddClient(false)} title='Client Info' icon={UserPlus} width='max-w-[520px]'>
            <ClientForm
              clientData={
                isAutoQuoteFromEstimate && extractedClientName
                  ? { is_active: true, type: 'FLEET', name: extractedClientName }
                  : { is_active: true, type: 'FLEET' }
              }
              onSaveSuccess={fetchClients}
              clibrationCenterId={Number(ccId)}
              close={() => setIsAddClient(false)}
            />
          </FormModal>
        )}

        {/* Page header — desktop only; mobile uses the global nav header + native back */}
        <AdasBox className='mb-6 hidden min-[435px]:block'>
          {(() => {
            const estimateId = location.state?.estimateSource?.id || location.state?.recentEstimate?.id
            const fromEstimate = !!estimateId
            const backLabel = fromEstimate ? 'Back to Estimate Summary' : 'Back to Quotes List'
            return (
              <button
                type='button'
                onClick={() => history.goBack()}
                className='mb-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                {backLabel}
              </button>
            )
          })()}
          <AdasTypography className='text-[28px] font-bold leading-tight text-[#0A1628]'>{title || 'New Quotes'}</AdasTypography>
        </AdasBox>

        {/* Step 1 — Client & repair order */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={1} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Client &amp; repair order</AdasTypography>
          </AdasBox>

          {/* Select client row — DO NOT change style of the dropdown or the [+] button.
              Auto Quote shows every client for the calibration center; fuzzy matches
              against the BE-extracted name are sorted to the top and the best match
              is preselected. */}
          <AdasBox className='mb-5'>
            <FieldLabel required>Client</FieldLabel>
            <AdasBox className='flex items-stretch w-full'>
              <AdasSelect
                labelId='clients'
                id='client-select'
                name='client'
                value={formInputValues.client}
                onChange={autocompleteHandler}
                options={clientDropdownOptions}
                className='flex-1 min-w-0 text-left rounded-r-none border-r-0'
              />
              <button
                type='button'
                onClick={() => setIsAddClient(true)}
                aria-label='Add client'
                className='inline-flex items-center justify-center h-11 w-12 rounded-r-lg rounded-l-none bg-sidebar text-white hover:bg-sidebar/90 border border-l-0 border-sidebar transition-colors shrink-0'
              >
                <Plus className='h-5 w-5' />
              </button>
            </AdasBox>
          </AdasBox>

          {/* Repair order toggle */}
          <AdasBox className='mb-5'>
            <FieldLabel>Repair order</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { label: 'RO Provided', value: false },
                { label: 'Customer Pay', value: true }
              ]}
              name='clientPay'
              value={customerPay}
              onChange={handleCustomerPay}
            />
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel required>RO #</FieldLabel>
            <AdasTextField
              id='outlined-basic'
              variant='outlined'
              name='roNumber'
              onChange={changeHandler}
              value={formInputValues.roNumber}
              disabled={customerPay}
              placeholder='RO #'
              inputProps={{ maxLength: 12 }}
              fullWidth
            />
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel required>Insurance Company</FieldLabel>
            <AdasAutoComplete
              labelId='insurance_label'
              id='insurance_company_id'
              name='insurance_company_id'
              value={formInputValues.insurance_company_id}
              onChange={autocompleteHandler}
              options={insuranceCompanies || []}
              className='w-full text-left'
              placeholder='Insurance Company (Required)'
            />
          </AdasBox>

          <AdasBox>
            <FieldLabel>Type</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { value: 'STANDARD', label: 'Standard' },
                { value: 'SERVICE_ONLY', label: 'Service Only' }
              ]}
              name='serviceType'
              value={formInputValues.serviceType}
              onChange={changeHandler}
            />
          </AdasBox>

          {!autoMatchedReferral?.id && (
            <>
              {/* future reference commented below code */}
              {/* <AdasBox className="text-left my-4 mx-4">
                <AdasFormLabel id='referral-source-label' align='left'>
                  Do you have a referral source?
                </AdasFormLabel>
                <AdasRadioGroup
                  row
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                  ]}
                  disabled={!!referralSourceCode}
                  name='referral-radio-group'
                  value={showReferralField}
                  onChange={handleShowReferralField}
                />
              </AdasBox> */}
              {/* future reference commented below code */}
              {/* {showReferralField && (
                <>
                  <AdasFormControl fullWidth>
                    {referralSourceList.length > 0 ? (
                      <>
                        <AdasInputLabel id='referral_label'>Referral Source</AdasInputLabel>
                        <AdasSelect
                          labelId='referral_label'
                          id='referral_label'
                          name='referral_source_code'
                          label='Referral Source'
                          value={formInputValues.referral_source_code || ''}
                          onChange={autocompleteHandler}
                          options={referralSourceList}
                          className="w-full text-left"
                        />
                      </>
                    ) : !referralSourceCode ? (
                      <>
                        <AdasAlert
                          severity='warning'
                          className="rounded border border-[#FFE58F] text-[#614700] bg-[#FFFBE6]"
                        >
                          {' '}
                          Please contact support to enroll in National Agreements
                        </AdasAlert>
                      </>
                    ) : null}
                  </AdasFormControl>
                </>
              )} */}
            </>
          )}
        </AdasBox>

        {/* Step 2 — Vehicle */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={2} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Vehicle</AdasTypography>
          </AdasBox>

          <AdasBox className='mb-5'>
            <FieldLabel>Do you have a VIN?</FieldLabel>
            <AdasRadioGroup
              row
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
              aria-labelledby='controlled-radio-buttons-group'
              name='isVinAvailable'
              value={isVinAvailable}
              onChange={handleVinAvailableToggle}
            />
          </AdasBox>

          {isVinAvailable ? (
            <AdasBox key='vin-section'>
              <FieldLabel required>VIN</FieldLabel>
              <VinInput updateVehicleInfo={updateVehicleInfo} isQuote={true} prepopulateVin={prePopulateVin} vinExistCheck={true} />
              {displayDetails ? (
                <VehicleInfoDetails isQuote={true} data={vehicleData} />
              ) : (
                <AdasBox className='mt-4'>
                  <CarImageHolder showText={false} />
                  <AdasBox className='mt-4 rounded-lg bg-[#EBEDF0] py-3 text-center'>
                    <AdasTypography className='text-[13px] text-[#475569]'>Enter VIN to fill vehicle details</AdasTypography>
                  </AdasBox>
                </AdasBox>
              )}
            </AdasBox>
          ) : (
            <VehicleInfoManuallPage
              formInputValues={formInputValues}
              setFormInputValues={setFormInputValues}
              handleVehicleManuallChange={handleVehicleManuallChange}
              manualOverRideVin={!isVinAvailable}
              isFromQuote={true}
            />
          )}
        </AdasBox>

        {/* Step 3 — Documents from client */}
        <AdasBox className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
          <AdasBox className='mb-5 flex items-center gap-3 border-b border-[#EBEDF0] pb-4'>
            <StepBadge n={3} />
            <AdasTypography className='text-[15px] font-bold text-[#0A1628]'>Documents from client</AdasTypography>
            <span className='ml-auto text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Optional</span>
          </AdasBox>
          <AdasBox className='flex flex-col gap-3'>
            <DocumentUploadRow
              name='preCalRepairEstimateScan'
              title='Collision repair estimate'
              subtitle='PDF from CCC, Mitchell, Audatex — used to reconstruct line items'
              existingData={documents.preCalRepairEstimateScan}
              onChange={documentChangeHandler}
            />
            <DocumentUploadRow
              name='calRequiredReport'
              title='Calibrations required report'
              subtitle='OEM scan tool report — confirms which calibrations the vehicle needs'
              existingData={documents.calRequiredReport}
              onChange={documentChangeHandler}
            />
          </AdasBox>
        </AdasBox>

        {/* Bottom action bar */}
        <AdasBox className='mt-6 flex flex-wrap items-center justify-end gap-3'>
          <AdasButton variant='outlined' aria-label='Cancel' onClick={() => history.goBack()} className='rounded-lg px-6 font-semibold'>
            CANCEL
          </AdasButton>
          <AdasButton
            disabled={!isEnable}
            variant='contained'
            aria-label='Create Quote'
            isDebounceEnabled={true}
            onClick={() => submitHandler()}
            className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
          >
            CREATE QUOTE
          </AdasButton>
        </AdasBox>
        {renderVehicleInProgress()}
      </AdasBox>
    </AdasContainer>
  )
}

NewQuote.propTypes = {
  title: PropTypes.string
}
